import { useEffect, useMemo, useState } from 'react';
import type { PartialTheme } from '@nivo/theming';
import { themeColor, type TokenName } from '~/lib/theme-colors';

/**
 * Chart colors resolved from the design tokens, and the `.theme-dark` flag they depend on.
 * Nivo cannot read Tailwind classes, so this is the one bridge: spread `nivoTheme` into a Nivo
 * chart's `theme` prop. Colors are empty strings until the first effect has run.
 *
 * grid / track: gray-200 light, gray-700 dark. axisLabel: gray-500 light, gray-400 dark.
 * text: gray-700 light, gray-300 dark.
 */
export function useChartTheme() {
  const [isDark, setIsDark] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const update = () => setIsDark(document.documentElement.classList.contains('theme-dark'));
    update();
    setReady(true);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return useMemo(() => {
    const color = (name: TokenName) => (typeof document === 'undefined' ? '' : themeColor(name));
    const pick = (light: TokenName, dark: TokenName) => (ready ? color(isDark ? dark : light) : '');
    const grid = pick('gray-200', 'gray-700');
    const axisLabel = pick('gray-500', 'gray-400');
    const text = pick('gray-700', 'gray-300');
    const nivoTheme: PartialTheme = {
      text: { fill: text },
      axis: {
        domain: { line: { stroke: grid } },
        ticks: { line: { stroke: grid }, text: { fill: axisLabel } },
        legend: { text: { fill: text } },
      },
      grid: { line: { stroke: grid } },
      legends: { text: { fill: text } },
      crosshair: { line: { stroke: axisLabel } },
      tooltip: { container: { background: pick('gray-25', 'gray-800'), color: text } },
    };
    return { isDark, grid, axisLabel, text, track: grid, color, nivoTheme };
  }, [isDark, ready]);
}
